import React from 'react';
import {
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { RELATION_PART_OF } from '@backstage/catalog-model';
import { InfoCard } from '@backstage/core-components';
import { Grid, Chip, Typography, Link } from '@material-ui/core';
import type { Entity } from '@backstage/catalog-model';

const ANNOTATION_PROJECT_ID = 'busy.no/project-id';
const ANNOTATION_LOG_DRAIN = 'try.no/log-drain';
const ANNOTATION_JIRA_PROJECT_SLUG = 'jira.com/project-key';
const ANNOTATION_SLA_AGREEMENT = 'try.no/sla-agreement';
const ANNOTATION_SLA_TIER = 'try.no/sla-tier';
const ANNOTATION_SLA_CLIENT_DOMAIN = 'try.no/sla-client-domain';
const ANNOTATION_DOCUMENTATION_URL = 'try.no/documentation';
const ANNOTATION_PROJECT_OWNER = 'try.no/project-owner';

const JIRA_BASE_URL = 'https://tryoslo.atlassian.net/browse';

const PROJECT_ANNOTATION_KEYS = [
  ANNOTATION_PROJECT_ID,
  ANNOTATION_LOG_DRAIN,
  ANNOTATION_JIRA_PROJECT_SLUG,
  ANNOTATION_SLA_AGREEMENT,
  ANNOTATION_SLA_TIER,
  ANNOTATION_SLA_CLIENT_DOMAIN,
  ANNOTATION_DOCUMENTATION_URL,
  ANNOTATION_PROJECT_OWNER,
] as const;

/**
 * Merges annotations from the current entity with the parent system.
 * Component-level values take precedence; missing keys fall back to the system.
 *
 * @param entity - Current entity (component or system).
 * @param systemEntity - Parent system entity when entity is a component.
 * @returns Merged annotations object.
 */
function mergeAnnotations(
  entity: Entity,
  systemEntity: Entity | undefined,
): Record<string, string | string[] | undefined> {
  const current = entity.metadata.annotations ?? {};
  if (!systemEntity) {
    return { ...current };
  }
  const system = systemEntity.metadata.annotations ?? {};
  const merged: Record<string, string | string[] | undefined> = { ...current };
  for (const key of PROJECT_ANNOTATION_KEYS) {
    if (merged[key] === undefined || merged[key] === '') {
      const systemVal = system[key];
      if (systemVal !== undefined && systemVal !== '') {
        merged[key] = systemVal;
      }
    }
  }
  return merged;
}

/**
 * Normalizes the busy.no/project-id annotation to an array of strings.
 * Supports a single value (string), comma-separated string, or array.
 *
 * @param value - Raw annotation value (string or string[]).
 * @returns Array of trimmed project ID strings, or empty array if none.
 */
function normalizeProjectIds(value: string | string[] | undefined): string[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string')
      .map(v => v.trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Normalizes try.no/sla-client-domain to an array of strings.
 * Supports a single value (string), comma-separated string, or array.
 *
 * @param value - Raw annotation value (string or string[]).
 * @returns Array of trimmed domain strings, or empty array if none.
 */
function normalizeSlaClientDomains(
  value: string | string[] | undefined,
): string[] {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string')
      .map(v => v.trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Card that displays project-related annotations in three columns:
 * Busy.no project IDs; Log drain and Jira links; SLA (agreement, tier, client domain).
 * Uses annotations: busy.no/project-id, try.no/log-drain, jira.com/project-slug,
 * try.no/sla-agreement, try.no/sla-tier, try.no/sla-client-domain.
 * When the entity is a component, missing annotations fall back to the parent system.
 */
export function EntityProjectIdCard(): React.ReactElement {
  const { entity } = useEntity();
  const { entities: relatedSystems } = useRelatedEntities(entity, {
    type: RELATION_PART_OF,
    kind: 'system',
  });
  const systemEntity =
    entity.kind !== 'system' && relatedSystems?.length
      ? (relatedSystems[0] as Entity)
      : undefined;
  const annotations = mergeAnnotations(entity, systemEntity);

  const projectIds = normalizeProjectIds(
    annotations[ANNOTATION_PROJECT_ID] as string | string[] | undefined,
  );
  const logDrainUrl = (annotations[ANNOTATION_LOG_DRAIN] as string | undefined)
    ?.trim();
  const jiraSlug = (annotations[ANNOTATION_JIRA_PROJECT_SLUG] as string | undefined)
    ?.trim();
  const slaAgreementUrl = (
    annotations[ANNOTATION_SLA_AGREEMENT] as string | undefined
  )?.trim();
  const slaTier = (annotations[ANNOTATION_SLA_TIER] as string | undefined)?.trim();
  const slaClientDomains = normalizeSlaClientDomains(
    annotations[ANNOTATION_SLA_CLIENT_DOMAIN] as string | string[] | undefined,
  );
  const documentationUrl = (annotations[ANNOTATION_DOCUMENTATION_URL] as string | undefined)?.trim();
  const projectOwner = (annotations[ANNOTATION_PROJECT_OWNER] as string | undefined)?.trim();
  const hasLogDrainOrJira = Boolean(logDrainUrl || jiraSlug);
  const hasSla =
    Boolean(slaAgreementUrl) ||
    Boolean(slaTier) ||
    slaClientDomains.length > 0;

  return (
    <InfoCard title="Project" variant="gridItem">
      <Grid container spacing={2}>
        {/* Column 1: Busy.no project IDs */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Project
          </Typography>
          {projectIds.length === 0 ? (
            <Typography variant="body2">—</Typography>
          ) : (
            <Grid container spacing={1}>
              {projectIds.map(id => (
                <Grid item key={id}>
                  <Chip
                    label={id}
                    size="small"
                    variant="outlined"
                    component="span"
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {projectOwner && (
            <Grid item>
              <Typography variant="body2">Owner: {projectOwner}</Typography>
            </Grid>
          )}
        </Grid>

        {/* Column 2: Log drain + Jira */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Resources
          </Typography>
          {!hasLogDrainOrJira ? (
            <Typography variant="body2">—</Typography>
          ) : (
            <Grid container spacing={1} direction="column">
              {logDrainUrl && (
                <Grid item>
                  <Link
                    href={logDrainUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Log drain
                  </Link>
                </Grid>
              )}
              {jiraSlug && (
                <Grid item>
                  <Link
                    href={`${JIRA_BASE_URL}/${jiraSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Jira ({jiraSlug})
                  </Link>
                </Grid>
              )}
              {documentationUrl && (
                <Grid item>
                  <Link
                    href={documentationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Documentation
                  </Link>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        {/* Column 3: SLA */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            SLA
          </Typography>
          {!hasSla ? (
            <Typography variant="body2">—</Typography>
          ) : (
            <Grid container spacing={1} direction="column">
              {slaAgreementUrl && (
                <Grid item>
                  <Link
                    href={slaAgreementUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    Agreement
                  </Link>
                </Grid>
              )}
              {slaTier && (
                <Grid item>
                  <Typography variant="body2">Tier: {slaTier}</Typography>
                </Grid>
              )}
              {slaClientDomains.length > 0 && (
                <Grid item>
                  <Typography variant="body2" component="span">
                    Client domain{slaClientDomains.length !== 1 ? 's' : ''}:{' '}
                  </Typography>
                  {slaClientDomains.map(domain => (
                    <Chip
                      key={domain}
                      label={domain}
                      size="small"
                      variant="outlined"
                      component="span"
                      style={{ marginRight: 4, marginBottom: 4 }}
                    />
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </InfoCard>
  );
}
